import { useCallback, useEffect, useRef, useState } from 'react';

import { getApiErrorInfo, getRetryAfterMs } from '@common/utils/fetcher';

const LOAD_MORE_RETRY_MS = 2 * 1000;
const LOAD_MORE_RETRY_MAX_MS = 60 * 1000;

/**
 * The wait after the `failuresInARow`-th failed page in a row: the server's
 * Retry-After for a 429 that has one, otherwise 2 s doubling up to 60 s.
 */
export const getLoadMoreRetryDelayMs = (
  error: unknown,
  failuresInARow: number,
) => {
  const retryAfterMs =
    getApiErrorInfo(error).status === 429 ? getRetryAfterMs(error) : null;

  if (retryAfterMs !== null) return retryAfterMs;

  return Math.min(
    LOAD_MORE_RETRY_MS * 2 ** Math.max(0, failuresInARow - 1),
    LOAD_MORE_RETRY_MAX_MS,
  );
};

/**
 * The back-off of an infinite-scroll loader. Its sentinel's observer fires
 * as soon as it is created again while the sentinel is in view, so without
 * this a failed page is requested again about once per round trip while the
 * user stays at the edge of the list.
 *
 * After a failure `isBackingOff` is true for the delay above. Keep the
 * observer off while it is: when the delay ends the observer is created
 * again, and fires if the sentinel is still in view. Report the failure
 * before the loader leaves its loading state. That alone does not keep the
 * two in one render (a store's update can render before this state's), so
 * also check `canLoadMore` when the observer fires. A success resets the
 * delay.
 */
export const useLoadMoreBackoff = () => {
  const [isBackingOff, setIsBackingOff] = useState(false);
  const isBackingOffRef = useRef(false);
  const failuresInARowRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const onLoadFailure = useCallback((error: unknown) => {
    failuresInARowRef.current += 1;
    const delayMs = getLoadMoreRetryDelayMs(error, failuresInARowRef.current);

    clearTimeout(timerRef.current);
    isBackingOffRef.current = true;
    setIsBackingOff(true);
    timerRef.current = setTimeout(() => {
      isBackingOffRef.current = false;
      setIsBackingOff(false);
    }, delayMs);
  }, []);

  const onLoadSuccess = useCallback(() => {
    failuresInARowRef.current = 0;
  }, []);

  const canLoadMore = useCallback(() => !isBackingOffRef.current, []);

  return { isBackingOff, canLoadMore, onLoadFailure, onLoadSuccess };
};
